import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import useFetch from 'use-http';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { InfoButton } from '@/components/ui/shared/InfoButton';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import { Badge } from '@/components/ui/badge';

export default function OtherVisitsTable() {
  const { t } = useTranslation();
  const history = useHistory();
  const sessionDispatch = useUserDispatch();
  const userState = useUserState();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(userState.dateFilter);

  const { get: getVisits, response: getVisitsResponse } = useFetch(
    '/UserAccount/Visits',
  );

  const fetchData = async (
    date = dateRange,
    term = search,
    status = 'other',
  ) => {
    setIsLoading(true);
    sessionDispatch({ type: 'UPDATE', data: { dateFilter: date } });

    const query = getQueryString(
      date,
      term,
      status,
      undefined,
      userState.espbiEsi,
    );
    const res = await getVisits(query);

    if (getVisitsResponse.ok) {
      const sorted = _.orderBy(res ?? [], ['date'], ['desc']);
      setData(sorted);
    }

    setIsLoading(false);
  };

  const handleSelection = (visit) => {
    history.push('/userAccount/visits/details', {
      visit,
      fromOtherVisits: true,
    });
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  return (
    <DataTable
      title="Kitos paslaugos"
      loading={isLoading}
      data={data}
      columns={columns({ handleSelection })}
      filterableColumns={[
        'registrationService',
        'department',
        'paymentStatus',
        'date',
      ]}
      search={search}
      onSearchChange={(val) => {
        setSearch(val);
        fetchData(dateRange, val);
      }}
      dateRange={dateRange}
      onDateRangeChange={(range) => {
        setDateRange(range);
        fetchData(range, search);
      }}
      onRowClick={(row) => handleSelection(row)}
    />
  );
}

const columns = ({ handleSelection }) => [
  {
    accessorKey: 'visitTitle',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="PASLAUGA" />
    ),
    cell: ({ row }) => {
      const label = row.original.registrationService || row.original.visitTitle;
      return (
        <span className="font-medium max-w-[400px] truncate">{label}</span>
      );
    },
  },
  {
    accessorKey: 'date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => {
      const d = new Date(row.original.date);
      const isMidnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0;
      return (
        <span className="text-muted-foreground">
          {isMidnight ? format(d, 'yyyy-MM-dd') : format(d, 'yyyy-MM-dd HH:mm')}
        </span>
      );
    },
  },
  {
    accessorKey: 'department',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="SKYRIUS" />
    ),
    cell: ({ row }) => {
      const dept = row.original.department || '';
      const addr = row.original.departmentAddress || '';
      return (
        <span className="text-muted-foreground">
          {dept}
          {addr ? `, ${addr}` : ''}
        </span>
      );
    },
  },
  {
    accessorKey: 'paymentStatus',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="BŪSENA" />
    ),
    cell: ({ row }) => {
      const display = row.original.paymentStatus;
      let variant = 'neutral';
      if (display === 'Įvykusi') variant = 'success';
      else if (display === 'Neįvykęs') variant = 'fail';
      return <Badge variant={variant}>{display}</Badge>;
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex justify-end">
        <InfoButton
          onClick={(e) => {
            e.stopPropagation(); // ✅ Prevent row click
            handleSelection(row.original);
          }}
        >
          Informacija
        </InfoButton>
      </div>
    ),
  },
];
